import { Euler, Matrix4, Raycaster, Vector3 } from "three";
import { Camera } from "../scenegraph/internal";
import { GeometryUtils } from "../utils/internal";
import { isDefined } from "../internal";
import { Behavior } from "./internal";
/**
 * @class PanoCameraBehaviour
 * @param {SceneGraphNode} parentObject
 * @constructor
 */
export class PanoCameraBehavior extends Behavior {
    /**
     * @method constructor
     * @param {SceneGraphNode} parentObject
     * @param {number[]} position
     * @param {number[]} orientation
     */
    constructor(parentObject, position, orientation) {
        super(parentObject);
        /**
         * @property {Vector3} position
         */
        this.positionAbsolute = new Vector3();
        this.zoom = 1.0;
        /**
         * @property {object} cameraIntrinsics
         */
        this.cameraIntrinsics = {
            IMAGE_WIDTH: 2048,
            IMAGE_HEIGHT: 2448,
            PIXEL_SIZE: 0.0034,
            NEAR: 0.1,
            FAR: 10000,
            MIN_ZOOM: 1,
            MAX_ZOOM: 2,
        };
        /**
         *
         * @type {boolean}
         * @private
         */
        this._positionChanged = false;
        this.cameraGroup = null;
        this._fov = 70;
        this._nFov = 70;
        this._maxFov = 100;
        this._minFov = 10;
        this._oDist = 0;
        this.lat = 0;
        this.lon = 0;
        this._nLat = 0;
        this._nLon = 0;
        this._cd = new Date();
        this._time = this._cd.getTime();
        this.camposchanged = false;
        this.rotVec = new Vector3(0.0, 0.0, 0.0);
        this.isUserInteracting = false;
        this._tMouseVector = new Vector3();
        this.onRotate = null;
        this.onZoom = null;
        this.position = new Vector3(position[0], position[1], position[2]);
        this.orientation = new Vector3(orientation[0], orientation[1], orientation[2]);
        if (!(parentObject instanceof Camera)) {
            throw "Wrong Parent Object: SceneCamera Behavior must be attached to a Camera object.";
        }
    }
    setPosition(position) {
        this.position.set(position[0], position[1], position[2]);
        this._positionChanged = true;
    }
    setOrientation(orientation) {
        this.orientation.set(orientation[0], orientation[1], orientation[2]);
    }
    setCameraIntrinsics(cameraIntrinsics) {
        if (isDefined(cameraIntrinsics["IMAGE_WIDTH"])) {
            this.cameraIntrinsics["IMAGE_WIDTH"] = cameraIntrinsics["IMAGE_WIDTH"];
        }
        if (isDefined(cameraIntrinsics["IMAGE_HEIGHT"])) {
            this.cameraIntrinsics["IMAGE_HEIGHT"] = cameraIntrinsics["IMAGE_HEIGHT"];
        }
        if (isDefined(cameraIntrinsics["PIXEL_SIZE"])) {
            this.cameraIntrinsics["PIXEL_SIZE"] = cameraIntrinsics["PIXEL_SIZE"];
        }
        if (isDefined(cameraIntrinsics["NEAR"])) {
            this.cameraIntrinsics["NEAR"] = cameraIntrinsics["NEAR"];
            this.parentObject.iCamera.near = cameraIntrinsics["NEAR"];
        }
        if (isDefined(cameraIntrinsics["FAR"])) {
            this.cameraIntrinsics["FAR"] = cameraIntrinsics["FAR"];
            this.parentObject.iCamera.far = cameraIntrinsics["FAR"];
        }
        if (isDefined(cameraIntrinsics["MIN_ZOOM"])) {
            this.cameraIntrinsics["MIN_ZOOM"] = cameraIntrinsics["MIN_ZOOM"];
        }
        if (isDefined(cameraIntrinsics["MAX_ZOOM"])) {
            this.cameraIntrinsics["MAX_ZOOM"] = cameraIntrinsics["MAX_ZOOM"];
        }
        this.parentObject.iCamera.updateProjectionMatrix();
    }
    /**
     * Will be called in every render pass
     *
     * reads:   traversalstate.zoom
     *          traversalstate.panX
     *          traversalstate.panY
     *
     * writes:  traversalstate.actualcameraid
     *          traversalstate.panoCamChanged
     *          traversalstate.panoZoom
     *
     *
     * @method render
     * @param {TraversalState} traversalState
     */
    render(traversalState) {
        if (this.disable || traversalState.lockNavigation) {
            this.isUserInteracting = false;
            return;
        }
        traversalState.panoCamChanged = true;
        this.traversalState = traversalState;
        if (this.traversalState.resize) {
            this.parentObject.iCamera.aspect =
                traversalState.scene.engine.getContextWidth() / traversalState.scene.engine.getContextHeight();
        }
        if (traversalState.mouseDown) {
            const event = traversalState.mouseDownArguments;
            if (event) {
                event.preventDefault();
                this.isUserInteracting = true;
                this.onPointerDownPointerX = event.offsetX;
                this.onPointerDownPointerY = event.offsetY;
                this.onPointerDownLon = this.lon;
                this.onPointerDownLat = this.lat;
            }
        }
        if (traversalState.mouseUp) {
            const event = traversalState.mouseUpArguments;
            if (event)
                event.preventDefault();
            this.isUserInteracting = false;
        }
        if (traversalState.mouseMove) {
            const event = traversalState.mouseMoveArguments;
            if (event) {
                event.preventDefault();
                const f = this._fov / 350;
                if (this.isUserInteracting) {
                    const dx = (this.onPointerDownPointerX - event.offsetX) * f;
                    const dy = (event.offsetY - this.onPointerDownPointerY) * f;
                    this._nLon = dx + this.onPointerDownLon; // reversed dragging direction (thanks @mrdoob!)
                    this._nLat = dy + this.onPointerDownLat;
                }
            }
        }
        if (traversalState.mouseWheel) {
            let evt = traversalState.mouseWheelArguments;
            evt = evt ? evt : window.event;
            this._nFov = GeometryUtils.clamp(this._fov + 2.5 * evt.deltaY, this._minFov, this._maxFov);
            if (evt.type === "wheel" && evt.deltaY < 0) {
                // pan slightly to mouseposition on zooming in
                const f = this._nFov / 350;
                const dx = (evt.offsetX - evt.target.width / 2) * f;
                const dy = (evt.target.height / 2 - evt.offsetY) * f;
                this._nLon = dx / 8 + this.lon;
                this._nLat = dy / 8 + this.lat;
            }
        }
        const cd = new Date();
        const ctime = cd.getTime();
        this._ellapsedTime = GeometryUtils.clamp(ctime - this._time, 10, 100);
        this._ellapsedFactor = this._ellapsedTime / 16;
        const s = 0.15 * this._ellapsedFactor;
        let dlon = (this._nLon - this.lon) * s;
        if (Math.abs(dlon) < 0.001 || isNaN(dlon)) {
            this._nLon = this.lon;
            dlon = 0.0;
        }
        this.lon += dlon;
        let dlat = (this._nLat - this.lat) * s;
        if (Math.abs(dlat) < 0.001 || isNaN(dlat)) {
            this._nLat = this.lat;
            dlat = 0.0;
        }
        this.lat += dlat;
        if (isNaN(this.lat))
            this.lat = 0;
        if (isNaN(this.lon))
            this.lon = 0;
        traversalState.panoZoom = 1 - (this._fov - this._minFov) / (this._maxFov - this._minFov);
        traversalState.zoom = traversalState.panoZoom * 8;
        let dfov = (this._nFov - this._fov) * s;
        if (Math.abs(dfov) < 0.001) {
            this._nFov = this._fov;
            dfov = 0.0;
        }
        this._fov += dfov;
        if (dfov == 0 && dlat == 0 && dlon == 0 && !this.camposchanged) {
            traversalState.panoCamChanged = false;
            this.camposchanged = false;
        }
        this.parentObject.iCamera.fov = this._fov;
        this.parentObject.iCamera.updateProjectionMatrix();
        this.lat = Math.max(-85, Math.min(85, this.lat));
        this._phi = ((90 - this.lat) * Math.PI) / 180;
        this._theta = ((-90 + this.lon) * Math.PI) / 180;
        // Set up View Matrix
        const world2camera = new Matrix4()
            .makeRotationFromEuler(new Euler((this.orientation.x * Math.PI) / 180, (this.orientation.y * Math.PI) / 180, (this.orientation.z * Math.PI) / 180, "XYZ"))
            .transpose();
        //stabilization
        const rollpitchheading = new Euler().setFromRotationMatrix(new Matrix4().multiplyMatrices(new Matrix4().copy(world2camera).transpose(), new Matrix4().makeRotationFromEuler(new Euler(-Math.PI / 2, 0, 0))), "ZYX");
        const camera2horizon = new Matrix4().makeRotationFromEuler(new Euler(-rollpitchheading.x, 0.0, rollpitchheading.y, "ZYX"));
        const latlon = new Matrix4().makeRotationFromEuler(new Euler(this.lat * (Math.PI / 180), -this.lon * (Math.PI / 180), 0, "ZYX"));
        this.parentObject.iCamera.setRotationFromMatrix(new Matrix4().multiplyMatrices(camera2horizon, latlon));
        this.parentObject.iCamera.updateProjectionMatrix();
        this._time = ctime;
        if (this.lat != this.latprev || this.lon != this.lonprev) {
            traversalState.lockRendering = true;
            if (this.onRotate != null)
                this.onRotate(this.lat, this.lon);
        }
        this.latprev = this.lat;
        this.lonprev = this.lon;
        const rot = this.parentObject.iCamera.rotation;
        const Rcam = new Matrix4();
        Rcam.makeRotationFromEuler(new Euler(rot.x, rot.y, rot.z));
        Rcam.transpose();
        Rcam.multiply(world2camera.clone());
        traversalState.matProjection = this.parentObject.iCamera.projectionMatrix;
        const matmodel = new Matrix4();
        matmodel.elements[12] = -(this.position.x - traversalState.scene.engine.centroid[0]);
        matmodel.elements[13] = -(this.position.y - traversalState.scene.engine.centroid[1]);
        matmodel.elements[14] = -(this.position.z - traversalState.scene.engine.centroid[2]);
        traversalState.matModel = matmodel;
        traversalState.matModelView = world2camera.clone();
        traversalState.matModelView.multiply(matmodel.clone());
        const matmodelviewwcamrot = Rcam.clone().multiply(matmodel.clone());
        const matmvp = new Matrix4();
        traversalState.matMVP = matmvp.multiplyMatrices(traversalState.matProjection, matmodelviewwcamrot);
        traversalState.cameraIntrinsics = this.cameraIntrinsics;
        this.positionAbsolute.set(-matmodel.elements[12], -matmodel.elements[13], -matmodel.elements[14]);
        // raycasting
        // update the raycaster for picking
        this._tMouseVector.x =
            2 * (traversalState.actualMousePos[0] / traversalState.scene.engine.getContextWidth()) - 1;
        this._tMouseVector.y =
            1 - 2 * (traversalState.actualMousePos[1] / traversalState.scene.engine.getContextHeight());
        // set two vectors with opposing z values
        this._tMouseVector.z = -1.0;
        let vector = this._tMouseVector.clone();
        const camera = this.parentObject.iCamera;
        let end = new Vector3(vector.x, vector.y, 1.0);
        vector = vector.unproject(camera);
        end = end.unproject(camera);
        // find direction from vector to end
        end.sub(vector).normalize();
        traversalState.rayCaster = new Raycaster(vector, end);
        traversalState.rayCaster.params.Points.threshold = Math.cos(traversalState.panoZoom) * 2;
        // distance in welcher Punkte selektiert werden -> 5cm
        traversalState.panoCamPos = this.position;
    }
    /**
     *
     * @param lat
     * @param lon
     * @param directset if true -> camera changes without smooth animation....
     * @param fov
     */
    setLatLon(lat, lon, directset, fov) {
        let dlon = (lon % 360) - (this.lon % 360);
        if (dlon > 180)
            dlon = dlon - 360;
        lon = this.lon + dlon;
        let dlat = (lat % 360) - (this.lat % 360);
        if (dlat > 180)
            dlat = dlat - 360;
        lat = this.lat + dlat;
        this._nLon = lon;
        this._nLat = lat;
        if (fov) {
            if (fov < this._minFov) {
                fov = this._minFov;
            }
            if (fov > this._maxFov) {
                fov = this._maxFov;
            }
            this._nFov = fov;
        }
        if (directset) {
            this.lat = lat;
            this.lon = lon;
            if (fov)
                this._fov = fov;
        }
        this.camposchanged = true;
    }
    getLatLon() {
        return [
            Math.round(this.lat * 1000) / 1000,
            Math.round((this.lon % 360) * 1000) / 1000,
            Math.round(this._fov * 1000) / 1000,
        ];
    }
    setFovLimits(minFov, maxFov) {
        if (isDefined(minFov)) {
            this._minFov = minFov;
        }
        if (isDefined(maxFov)) {
            this._maxFov = maxFov;
        }
    }
}
//# sourceMappingURL=panocamerabehavior.js.map